import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Toggle} from '../../../../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {TimeFormatsEnum} from '../../../../../helpers/dateTimeHelpers';
import {CalendarEventDetailRow} from '../../../../calendars/viewer/calendarEventDetailRow';
import {TimezoneDropdownBox} from './timezoneDropdownBox';

/*
 * Props.
 */

interface MeetingFormattingSettingsProps {
  timezone: string;
  timeFormat: TimeFormatsEnum;
  onTimezoneSelect: HandlerOf<string>;
  onTimeFormatSelect: HandlerOf<TimeFormatsEnum>;
}

/*
 * Styles.
 */

const StyledContainerDiv = styled.div`
  display: grid;
  grid-auto-rows: min-content;
  grid-row-gap: 12px;
`;

const StyledFormattingHeaderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
`;

const StyledSectionIcon = styled(Icon)`
  margin-top: 7px;
`;

const StyledDropdownWrapperDiv = styled.div`
  > div {
    padding: 0px;
  }
`;

const Styled24HourTimeDiv = styled.div`
  padding: 0px 2px 2px 4px;
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  display: grid;
  grid-template-areas: 'title toggle';
  grid-template-columns: 1fr auto;
  align-items: center;
`;

/*
 * Component.
 */

export const MeetingFormattingSettings: FC<MeetingFormattingSettingsProps> = (props) => (
  <StyledContainerDiv>
    <StyledFormattingHeaderDiv>
      <Trans id="scheduling-meetingsettings-formatting">Formatting</Trans>
    </StyledFormattingHeaderDiv>
    {renderTimezoneSection(props.timezone, props.onTimezoneSelect)}
    {render24HourTimeSection(props.timeFormat, props.onTimeFormatSelect)}
  </StyledContainerDiv>
);

function renderTimezoneSection(timezone: string, onUpdateTimezone: HandlerOf<string>) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="globeSmall" color={theme.greys.shade70} />}
          content={
            <StyledDropdownWrapperDiv>
              <TimezoneDropdownBox timezone={timezone} onTimezoneSelect={onUpdateTimezone} />
            </StyledDropdownWrapperDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

function render24HourTimeSection(timeFormat: TimeFormatsEnum, setTimeFormat: HandlerOf<TimeFormatsEnum>) {
  return (
    <Styled24HourTimeDiv>
      <Trans id="scheduling-meetingsettings-24hourtime">24hr format</Trans>
      <Toggle
        size={VisualSizesEnum.SMALL}
        isChecked={timeFormat === TimeFormatsEnum.H24}
        onChange={(selected) => setTimeFormat(selected ? TimeFormatsEnum.H24 : TimeFormatsEnum.H12)}
      />
    </Styled24HourTimeDiv>
  );
}
